import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "reactstrap";
import { SwitchToggle } from "../../../Components/TableHelper";
import { useFormik } from "formik";
import { amcplanSchema } from "../../../Validation/Validation";
import axiosInstance from "../../../axiosInterceptor";
import { toast } from "react-toastify";
import Breadcrumb from "../../../Components/Breadcrumb";
import CodeEditor from "../../../Components/CodeEditor";
import ImageUploadDragAndDrop from "../../../Components/ImageUploadDragAndDrop";

const emptyPricingRow = {
  category: "",
  floorConfiguration: "",
  price: "",
  pricePeriod: "yearly",
  pricingType: "fixed",
  gstApplicable: true,
};

const AddAmcPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state;
  const editingId = editData?._id;
  const [loading, setLoading] = useState(false);

  const initialValues = {
    name: editData?.name || "",
    sortOrder: editData?.sortOrder || 1,
    shortDescription: editData?.shortDescription || "",
    icon: editData?.icon || "",
    badge: editData?.badge || "",
    description: editData?.description || "",
    isActive: editData?.isActive ?? true,
    // empty array by default: no pricing row is forced
    pricing: Array.isArray(editData?.pricing)
      ? editData.pricing.map((p) => ({
        category: p.category || "",
        floorConfiguration: p.floorConfiguration || "",
        price: p.price ?? "",
        pricePeriod: p.pricePeriod || "yearly",
        pricingType: p.pricingType || "fixed",
        gstApplicable: p.gstApplicable ?? true,
      }))
      : [],
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setTouched,
    setErrors,
  } = useFormik({
    initialValues,
    validationSchema: amcplanSchema,
    onSubmit: async (formValues) => {
      setLoading(true);
      try {
        const pricing = formValues.pricing.map((p) => ({
          category: p.category.trim(),
          floorConfiguration: p.floorConfiguration.trim(),
          pricingType: p.pricingType,
          pricePeriod: p.pricePeriod,
          gstApplicable: p.gstApplicable,
          price:
            p.pricingType === "custom" || p.price === ""
              ? null
              : String(p.price).trim(),
        }));

        const formData = new FormData();
        formData.append("sortOrder", formValues.sortOrder);
        formData.append("description", formValues.description);
        formData.append("name", formValues.name);
        formData.append("shortDescription", formValues.shortDescription);
        formData.append("badge", formValues.badge);
        formData.append("isActive", formValues.isActive || false);
        // arrays/objects must be sent as a JSON string inside FormData ("[]" when empty)
        formData.append("pricing", JSON.stringify(pricing));
        if (formValues.icon instanceof File) {
          formData.append("icon", formValues.icon);
        }

        const response = editingId
          ? await axiosInstance.put(`/amcplan/updateamcplan/${editingId}`, formData)
          : await axiosInstance.post("/amcplan/createamcplan", formData);

        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/amc-plans");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  // ---------- pricing helpers ----------
  const addPricingRow = () =>
    setFieldValue("pricing", [...values.pricing, { ...emptyPricingRow }]);

  const removePricingRow = (index) => {
    setFieldValue(
      "pricing",
      values.pricing.filter((_, i) => i !== index),
    );

    // drop touched / error entries of the removed row so indexes stay in sync
    if (Array.isArray(touched.pricing)) {
      setTouched(
        {
          ...touched,
          pricing: touched.pricing.filter((_, i) => i !== index),
        },
        false,
      );
    }
    if (Array.isArray(errors.pricing)) {
      setErrors({
        ...errors,
        pricing: errors.pricing.filter((_, i) => i !== index),
      });
    }
  };

  const updatePricingRow = (index, field, value) => {
    const updated = values.pricing.map((row, i) =>
      i === index ? { ...row, [field]: value } : row,
    );
    // when switched to custom, clear the price
    if (field === "pricingType" && value === "custom") {
      updated[index].price = "";
    }
    setFieldValue("pricing", updated);
  };

  const getRowError = (index, field) =>
    touched.pricing?.[index]?.[field] && errors.pricing?.[index]?.[field];

  const breadcrumbdata = [
    { label: "Home", path: "/dashboard" },
    { label: "AMCPlan", path: "/amc-plans" },
    { label: editingId ? "Update" : "Add", path: "*" },
  ];

  return (
    <div className="content-wrapper">
      <div className="page-header mb-3">
        <h3 className="custom-page-title">
          {editingId ? "Update" : "Add"} AMCPlan
        </h3>
        <Breadcrumb items={breadcrumbdata} />
      </div>

      <div className="col-md-12 grid-margin stretch-card p-0">
        <div className="card m-0">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Sort order */}
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="amcplan-sortOrder" className="col-form-label">
                      Sort Order
                    </label>
                    <input
                      type="number"
                      className="border border-dark form-control"
                      id="amcplan-sortOrder"
                      name="sortOrder"
                      placeholder="Enter Sort Order"
                      min={1}
                      value={values.sortOrder}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.sortOrder && errors.sortOrder && (
                      <small className="text-danger">{errors.sortOrder}</small>
                    )}
                  </div>
                </div>

                {/* Name */}
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="amcplan-name" className="col-form-label">
                      Name
                    </label>
                    <input
                      type="text"
                      className="border border-dark form-control"
                      id="amcplan-name"
                      name="name"
                      placeholder="Enter amcplan name"
                      value={values.name || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.name && errors.name && (
                      <small className="text-danger">{errors.name}</small>
                    )}
                  </div>
                </div>

                {/* Short description */}
                <div className="col-sm-4">
                  <div className="form-group">
                    <label
                      htmlFor="amcplan-shortDescription"
                      className="col-form-label"
                    >
                      Short Description
                    </label>
                    <input
                      type="text"
                      className="border border-dark form-control"
                      id="amcplan-shortDescription"
                      name="shortDescription"
                      placeholder="Enter amcplan short description"
                      value={values.shortDescription || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.shortDescription && errors.shortDescription && (
                      <small className="text-danger">
                        {errors.shortDescription}
                      </small>
                    )}
                  </div>
                </div>

                {/* Badge */}
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="amcplan-badge" className="col-form-label">
                      Badge
                    </label>
                    <input
                      type="text"
                      className="border border-dark form-control"
                      id="amcplan-badge"
                      name="badge"
                      placeholder="Enter amcplan badge"
                      value={values.badge || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.badge && errors.badge && (
                      <small className="text-danger">{errors.badge}</small>
                    )}
                  </div>
                </div>

                {/* ---------- PRICING ---------- */}
                <div className="col-sm-12 mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="m-0">Pricing</h5>
                    <Button
                      type="button"
                      color="primary"
                      size="sm"
                      onClick={addPricingRow}
                    >
                      + Add Pricing
                    </Button>
                  </div>

                  {values.pricing.length === 0 && (
                    <p className="text-muted mb-3">
                      No pricing added. Click "+ Add Pricing" to add one.
                    </p>
                  )}

                  {values.pricing.map((row, index) => (
                    <div key={index} className="border rounded p-3 mb-3">
                      <div className="row">
                        {/* Category */}
                        <div className="col-sm-3">
                          <div className="form-group">
                            <label className="col-form-label">Category</label>
                            <input
                              type="text"
                              className="border border-dark form-control"
                              placeholder="e.g. Manual Door"
                              value={row.category}
                              onChange={(e) =>
                                updatePricingRow(index, "category", e.target.value)
                              }
                              onBlur={() =>
                                setFieldTouched(`pricing.${index}.category`, true)
                              }
                            />
                            {getRowError(index, "category") && (
                              <small className="text-danger">
                                {errors.pricing[index].category}
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Floor configuration */}
                        <div className="col-sm-3">
                          <div className="form-group">
                            <label className="col-form-label">
                              Floor Configuration
                            </label>
                            <input
                              type="text"
                              className="border border-dark form-control"
                              placeholder="e.g. Ground Floor + 5"
                              value={row.floorConfiguration}
                              onChange={(e) =>
                                updatePricingRow(
                                  index,
                                  "floorConfiguration",
                                  e.target.value,
                                )
                              }
                              onBlur={() =>
                                setFieldTouched(
                                  `pricing.${index}.floorConfiguration`,
                                  true,
                                )
                              }
                            />
                            {getRowError(index, "floorConfiguration") && (
                              <small className="text-danger">
                                {errors.pricing[index].floorConfiguration}
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Pricing type */}
                        <div className="col-sm-2">
                          <div className="form-group">
                            <label className="col-form-label">Pricing Type</label>
                            <select
                              className="border border-dark form-control"
                              value={row.pricingType}
                              onChange={(e) =>
                                updatePricingRow(index, "pricingType", e.target.value)
                              }
                            >
                              <option value="fixed">Fixed</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="col-sm-2">
                          <div className="form-group">
                            <label className="col-form-label">Price</label>
                            <input
                              type="number"
                              min={0}
                              className="border border-dark form-control"
                              placeholder={
                                row.pricingType === "custom" ? "Custom" : "Enter price"
                              }
                              value={row.price}
                              disabled={row.pricingType === "custom"}
                              onChange={(e) =>
                                updatePricingRow(index, "price", e.target.value)
                              }
                              onBlur={() =>
                                setFieldTouched(`pricing.${index}.price`, true)
                              }
                            />
                            {getRowError(index, "price") && (
                              <small className="text-danger">
                                {errors.pricing[index].price}
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Period */}
                        <div className="col-sm-2">
                          <div className="form-group">
                            <label className="col-form-label">Period</label>
                            <select
                              className="border border-dark form-control"
                              value={row.pricePeriod}
                              onChange={(e) =>
                                updatePricingRow(index, "pricePeriod", e.target.value)
                              }
                            >
                              <option value="yearly">Yearly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`gst-${index}`}
                            checked={row.gstApplicable}
                            onChange={(e) =>
                              updatePricingRow(index, "gstApplicable", e.target.checked)
                            }
                          />
                          <label className="form-check-label" htmlFor={`gst-${index}`}>
                            GST Applicable
                          </label>
                        </div>

                        {/* Always visible: any row can be removed, including the first */}
                        <Button
                          type="button"
                          color="danger"
                          size="sm"
                          onClick={() => removePricingRow(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}

                  {typeof errors.pricing === "string" && (
                    <small className="text-danger">{errors.pricing}</small>
                  )}
                </div>

                {/* Description */}
                <div className="col-sm-6">
                  <div className="form-group">
                    <label htmlFor="amcplan-description" className="col-form-label">
                      Description
                    </label>
                    <CodeEditor
                      value={values.description}
                      onChange={(val) => setFieldValue("description", val)}
                    />
                    {touched.description && errors.description && (
                      <small className="text-danger">{errors.description}</small>
                    )}
                  </div>
                </div>

                {/* Icon */}
                <div className="col-sm-6">
                  <ImageUploadDragAndDrop
                    id="amcplan-icon"
                    label="Icon (354px X 354px)"
                    value={values.icon}
                    onChange={(file) => setFieldValue("icon", file)}
                    error={errors.icon}
                    touched={touched.icon}
                  />
                </div>

                <div className="col-md-12 mt-5">
                  <SwitchToggle
                    checked={values.isActive}
                    onChange={(val) => setFieldValue("isActive", val)}
                  />
                </div>
              </div>

              <div className="text-center mt-4">
                <Button type="submit" color="primary" disabled={loading}>
                  {loading ? (
                    <span className="d-inline-flex align-items-center gap-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      Processing...
                    </span>
                  ) : (
                    "Save"
                  )}
                </Button>

                <Link to="/amc-plans">
                  <Button color="danger" className="mx-2">
                    Cancel
                  </Button>
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAmcPlan;

// import { useState } from "react";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { Button } from "reactstrap";
// import { SwitchToggle } from "../../../Components/TableHelper";
// import { useFormik } from "formik";
// import { amcplanSchema } from "../../../Validation/Validation";
// import axiosInstance from "../../../axiosInterceptor";
// import { toast } from "react-toastify";
// import Breadcrumb from "../../../Components/Breadcrumb";
// import CodeEditor from "../../../Components/CodeEditor";
// import ImageUploadDragAndDrop from "../../../Components/ImageUploadDragAndDrop";

// const emptyPricingRow = {
//   category: "",
//   floorConfiguration: "",
//   price: "",
//   pricePeriod: "yearly",
//   pricingType: "fixed",
//   gstApplicable: true,
// };

// const AddAmcPlan = () => {
//   const navigator = useNavigate();
//   const location = useLocation();
//   const editData = location.state;
//   const [loading, setLoading] = useState(false);
//   const editingId = editData?._id;

//   const initalvalues = {
//     name: editData?.name || "",
//     sortOrder: editData?.sortOrder || 1,
//     shortDescription: editData?.shortDescription || "",
//     icon: editData?.icon || "",
//     badge: editData?.badge || "",
//     description: editData?.description || "",
//     isActive: editData?.isActive ?? true,
//     pricing: editData?.pricing?.length
//       ? editData.pricing.map((p) => ({
//         category: p.category || "",
//         floorConfiguration: p.floorConfiguration || "",
//         price: p.price ?? "",
//         pricePeriod: p.pricePeriod || "yearly",
//         pricingType: p.pricingType || "fixed",
//         gstApplicable: p.gstApplicable ?? true,
//       }))
//       : []
//   };

//   const {
//     values,
//     errors,
//     handleChange,
//     handleSubmit,
//     setFieldValue,
//     touched,
//     handleBlur,
//   } = useFormik({
//     initialValues: initalvalues,
//     validationSchema: amcplanSchema,
//     onSubmit: async (values) => {
//       setLoading(true);
//       try {
//         const pricing = values.pricing.map((p) => ({
//           category: p.category.trim(),
//           floorConfiguration: p.floorConfiguration.trim(),
//           pricingType: p.pricingType,
//           pricePeriod: p.pricePeriod,
//           gstApplicable: p.gstApplicable,
//           price:
//             p.pricingType === "custom" || p.price === ""
//               ? null
//               : String(p.price).trim(),
//         }));

//         const formData = new FormData();
//         formData.append("sortOrder", values.sortOrder);
//         formData.append("description", values.description);
//         formData.append("name", values.name);
//         formData.append("shortDescription", values.shortDescription);
//         formData.append("badge", values.badge);
//         formData.append("isActive", values.isActive || false);
//         // arrays/objects must be sent as a JSON string inside FormData
//         formData.append("pricing", JSON.stringify(pricing));
//         if (values.icon instanceof File) {
//           formData.append("icon", values.icon);
//         }

//         const response = editingId
//           ? await axiosInstance.put(`/amcplan/updateamcplan/${editingId}`, formData)
//           : await axiosInstance.post("/amcplan/createamcplan", formData);

//         if (response.status === 200) {
//           toast.success(response.data.message);
//           navigator(`/amc-plans`);
//         }
//       } catch (error) {
//         toast.error(error.response?.data?.message);
//         return error;
//       } finally {
//         setLoading(false);
//       }
//     },
//   });

//   // ---- pricing helpers ----
//   const addPricingRow = () =>
//     setFieldValue("pricing", [...values.pricing, { ...emptyPricingRow }]);

//   const removePricingRow = (index) =>
//     setFieldValue(
//       "pricing",
//       values.pricing.filter((_, i) => i !== index)
//     );

//   const updatePricingRow = (index, field, value) => {
//     const updated = values.pricing.map((row, i) =>
//       i === index ? { ...row, [field]: value } : row
//     );
//     // when switched to custom, clear the price
//     if (field === "pricingType" && value === "custom") {
//       updated[index].price = "";
//     }
//     setFieldValue("pricing", updated);
//   };

//   const getRowError = (index, field) =>
//     touched.pricing?.[index]?.[field] && errors.pricing?.[index]?.[field];

//   const breadcrumbdata = [
//     { label: "Home", path: "/dashboard" },
//     { label: "AMCPlan", path: "/amc-plans" },
//     { label: editingId ? "Update" : "Add", path: "*" },
//   ];

//   return (
//     <>
//       <div className="content-wrapper">
//         <div className="page-header mb-3">
//           <h3 className="custom-page-title">
//             {editingId ? "Update" : "Add"} AMCPlan
//           </h3>
//           <Breadcrumb items={breadcrumbdata} />
//         </div>
//         <div className="col-md-12 grid-margin stretch-card p-0">
//           <div className="card m-0">
//             <div className="card-body">
//               <form onSubmit={handleSubmit}>
//                 <div className="row">
//                   <div className="col-sm-4">
//                     <div className="form-group">
//                       <label htmlFor="client-sortOrder" className="col-form-label">
//                         Sort Order
//                       </label>
//                       <input
//                         type="number"
//                         className="border border-dark form-control"
//                         id="client-sortOrder"
//                         name="sortOrder"
//                         placeholder="Enter Sort Order"
//                         min={1}
//                         value={values.sortOrder}
//                         onChange={handleChange}
//                       />
//                       {touched.sortOrder && errors.sortOrder && (
//                         <small className="text-danger">{errors.sortOrder}</small>
//                       )}
//                     </div>
//                   </div>

//                   <div className="col-sm-4">
//                     <div className="form-group">
//                       <label htmlFor="amcplan-name" className="col-form-label">
//                         Name
//                       </label>
//                       <input
//                         type="text"
//                         className="border border-dark form-control"
//                         id="amcplan-name"
//                         name="name"
//                         placeholder="Enter amcplan name"
//                         value={values.name || ""}
//                         onChange={handleChange}
//                         onBlur={handleBlur}
//                       />
//                       {touched.name && errors.name && (
//                         <small className="text-danger">{errors.name}</small>
//                       )}
//                     </div>
//                   </div>

//                   <div className="col-sm-4">
//                     <div className="form-group">
//                       <label
//                         htmlFor="amcplan-shortDescription"
//                         className="col-form-label"
//                       >
//                         Short Description
//                       </label>
//                       <input
//                         type="text"
//                         className="border border-dark form-control"
//                         id="amcplan-shortDescription"
//                         name="shortDescription"
//                         placeholder="Enter amcplan shortDescription"
//                         value={values.shortDescription || ""}
//                         onChange={handleChange}
//                         onBlur={handleBlur}
//                       />
//                     </div>
//                   </div>

//                   <div className="col-sm-4">
//                     <div className="form-group">
//                       <label htmlFor="amcplan-badge" className="col-form-label">
//                         Badge
//                       </label>
//                       <input
//                         type="text"
//                         className="border border-dark form-control"
//                         id="amcplan-badge"
//                         name="badge"
//                         placeholder="Enter amcplan badge"
//                         value={values.badge || ""}
//                         onChange={handleChange}
//                         onBlur={handleBlur}
//                       />
//                       {touched.badge && errors.badge && (
//                         <small className="text-danger">{errors.badge}</small>
//                       )}
//                     </div>
//                   </div>

//                   {/* ---------- PRICING ---------- */}
//                   <div className="col-sm-12 mt-3">
//                     <div className="d-flex justify-content-between align-items-center mb-2">
//                       <h5 className="m-0">Pricing</h5>
//                       <Button type="button" color="primary" size="sm" onClick={addPricingRow}>
//                         + Add Pricing
//                       </Button>
//                     </div>

//                     {values.pricing.map((row, index) => (
//                       <div key={index} className="border rounded p-3 mb-3">
//                         <div className="row">
//                           <div className="col-sm-3">
//                             <div className="form-group">
//                               <label className="col-form-label">Category</label>
//                               <input
//                                 type="text"
//                                 className="border border-dark form-control"
//                                 placeholder="e.g. Manual Door"
//                                 value={row.category}
//                                 onChange={(e) =>
//                                   updatePricingRow(index, "category", e.target.value)
//                                 }
//                                 onBlur={() =>
//                                   handleBlur({ target: { name: `pricing.${index}.category` } })
//                                 }
//                               />
//                               {getRowError(index, "category") && (
//                                 <small className="text-danger">
//                                   {errors.pricing[index].category}
//                                 </small>
//                               )}
//                             </div>
//                           </div>

//                           <div className="col-sm-3">
//                             <div className="form-group">
//                               <label className="col-form-label">Floor Configuration</label>
//                               <input
//                                 type="text"
//                                 className="border border-dark form-control"
//                                 placeholder="e.g. Ground Floor + 5"
//                                 value={row.floorConfiguration}
//                                 onChange={(e) =>
//                                   updatePricingRow(index, "floorConfiguration", e.target.value)
//                                 }
//                                 onBlur={() =>
//                                   handleBlur({
//                                     target: { name: `pricing.${index}.floorConfiguration` },
//                                   })
//                                 }
//                               />
//                               {getRowError(index, "floorConfiguration") && (
//                                 <small className="text-danger">
//                                   {errors.pricing[index].floorConfiguration}
//                                 </small>
//                               )}
//                             </div>
//                           </div>

//                           <div className="col-sm-2">
//                             <div className="form-group">
//                               <label className="col-form-label">Pricing Type</label>
//                               <select
//                                 className="border border-dark form-control"
//                                 value={row.pricingType}
//                                 onChange={(e) =>
//                                   updatePricingRow(index, "pricingType", e.target.value)
//                                 }
//                               >
//                                 <option value="fixed">Fixed</option>
//                                 <option value="custom">Custom</option>
//                               </select>
//                             </div>
//                           </div>

//                           <div className="col-sm-2">
//                             <div className="form-group">
//                               <label className="col-form-label">Price</label>
//                               <input
//                                 type="number"
//                                 min={0}
//                                 className="border border-dark form-control"
//                                 placeholder={
//                                   row.pricingType === "custom" ? "Custom" : "Enter price"
//                                 }
//                                 value={row.price}
//                                 disabled={row.pricingType === "custom"}
//                                 onChange={(e) =>
//                                   updatePricingRow(index, "price", e.target.value)
//                                 }
//                                 onBlur={() =>
//                                   handleBlur({ target: { name: `pricing.${index}.price` } })
//                                 }
//                               />
//                               {getRowError(index, "price") && (
//                                 <small className="text-danger">
//                                   {errors.pricing[index].price}
//                                 </small>
//                               )}
//                             </div>
//                           </div>

//                           <div className="col-sm-2">
//                             <div className="form-group">
//                               <label className="col-form-label">Period</label>
//                               <select
//                                 className="border border-dark form-control"
//                                 value={row.pricePeriod}
//                                 onChange={(e) =>
//                                   updatePricingRow(index, "pricePeriod", e.target.value)
//                                 }
//                               >
//                                 <option value="yearly">Yearly</option>
//                                 <option value="monthly">Monthly</option>
//                               </select>
//                             </div>
//                           </div>
//                         </div>

//                         <div className="d-flex justify-content-between align-items-center">
//                           <div className="form-check">
//                             <input
//                               type="checkbox"
//                               className="form-check-input"
//                               id={`gst-${index}`}
//                               checked={row.gstApplicable}
//                               onChange={(e) =>
//                                 updatePricingRow(index, "gstApplicable", e.target.checked)
//                               }
//                             />
//                             <label className="form-check-label" htmlFor={`gst-${index}`}>
//                               GST Applicable
//                             </label>
//                           </div>

//                           {values.pricing.length > 1 && (
//                             <Button
//                               type="button"
//                               color="danger"
//                               size="sm"
//                               onClick={() => removePricingRow(index)}
//                             >
//                               Remove
//                             </Button>
//                           )}
//                         </div>
//                       </div>
//                     ))}

//                     {typeof errors.pricing === "string" && (
//                       <small className="text-danger">{errors.pricing}</small>
//                     )}
//                   </div>

//                   <div className="col-sm-6">
//                     <div className="form-group">
//                       <label htmlFor="slider-description" className="col-form-label">
//                         Description
//                       </label>
//                       <CodeEditor
//                         value={values.description}
//                         onChange={(val) => setFieldValue("description", val)}
//                       />
//                       {touched.description && errors.description && (
//                         <small className="text-danger">{errors.description}</small>
//                       )}
//                     </div>
//                   </div>

//                   <div className="col-sm-6">
//                     <ImageUploadDragAndDrop
//                       id="slider-image"
//                       label={`Icon (354px X 354px)`}
//                       value={values.icon}
//                       onChange={(file) => setFieldValue("icon", file)}
//                       error={errors.icon}
//                       touched={touched.icon}
//                     />
//                   </div>

//                   <div className="col-md-12 mt-5">
//                     <SwitchToggle
//                       checked={values.isActive}
//                       onChange={(val) => setFieldValue("isActive", val)}
//                     />
//                   </div>
//                 </div>

//                 <div className="text-center mt-4">
//                   <Button type="submit" color="primary" disabled={loading}>
//                     {loading ? (
//                       <span className="d-inline-flex align-items-center gap-2">
//                         <span
//                           className="spinner-border spinner-border-sm"
//                           role="status"
//                           aria-hidden="true"
//                         ></span>
//                         Processing...
//                       </span>
//                     ) : (
//                       "Save"
//                     )}
//                   </Button>

//                   <Link to={`/amc-plans`}>
//                     <Button color="danger mx-2">Cancel </Button>
//                   </Link>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default AddAmcPlan;
// // import { useState } from "react";
// // import { Link, useLocation, useNavigate } from "react-router-dom";
// // import { Button } from "reactstrap";
// // import { SwitchToggle } from "../../../Components/TableHelper";
// // import { useFormik } from "formik";
// // import { amcplanSchema } from "../../../Validation/Validation";
// // import axiosInstance from "../../../axiosInterceptor";
// // import { toast } from "react-toastify";
// // import Breadcrumb from "../../../Components/Breadcrumb";
// // import CodeEditor from "../../../Components/CodeEditor";
// // import ImageUploadDragAndDrop from "../../../Components/ImageUploadDragAndDrop";
// // import Select from "react-select";
// // import { customStyles } from "../../../utils/constant";

// // const AddAmcPlan = () => {
// //   const navigator = useNavigate();
// //   const location = useLocation();
// //   const editData = location.state;
// //   const [loading, setLoading] = useState(false);
// //   const editingId = editData?._id;
// //   const initalvalues = {
// //     name: editData?.name || "",
// //     sortOrder: editData?.sortOrder || 1,
// //     shortDescription: editData?.shortDescription || "",
// //     icon: editData?.icon || "",
// //     monthlyPrice: editData?.monthlyPrice || "",
// //     yearlyPrice: editData?.yearlyPrice || "",
// //     badge: editData?.badge || "",
// //     description: editData?.description || "",
// //     isActive: editData?.isActive,
// //   };

// //   const {
// //     values,
// //     errors,
// //     handleChange,
// //     handleSubmit,
// //     setFieldValue,
// //     touched,
// //     handleBlur,
// //   } = useFormik({
// //     initialValues: initalvalues,
// //     validationSchema: amcplanSchema,
// //     onSubmit: async (values) => {
// //       setLoading(true);
// //       try {
// //         const formData = new FormData();

// //         formData.append("sortOrder", values.sortOrder);
// //         formData.append("description", values.description);
// //         formData.append("name", values.name);
// //         formData.append("shortDescription", values.shortDescription);
// //         formData.append("monthlyPrice", values.monthlyPrice);
// //         formData.append("yearlyPrice", values.yearlyPrice);
// //         formData.append("badge", values.badge);
// //         formData.append("isActive", values.isActive || false);
// //         if (values.icon instanceof File) {
// //           formData.append("icon", values.icon);
// //         }

// //         const response = editingId
// //           ? await axiosInstance.put(`/amcplan/updateamcplan/${editingId}`, formData)
// //           : await axiosInstance.post("/amcplan/createamcplan", formData);

// //         if (response.status === 200) {
// //           toast.success(response.data.message);
// //           navigator(`/amc-plans`);
// //         }
// //       } catch (error) {
// //         toast.error(error.response?.data?.message);
// //         return error;
// //       } finally {
// //         setLoading(false);
// //       }
// //     },
// //   });

// //   const Type = [
// //     { label: "LeaderShip", value: "LeaderShip" },
// //     { label: "Management", value: "Management" },
// //   ];

// //   const breadcrumbdata = [
// //     { label: "Home", path: "/dashboard" },
// //     { label: "AMCPlan", path: "/amc-plans" },
// //     { label: editingId ? "Update" : "Add", path: "*" },
// //   ];
// //   return (
// //     <>
// //       <div className="content-wrapper">
// //         <div className="page-header mb-3">
// //           <h3 className="custom-page-title">
// //             {editingId ? "Update" : "Add"} AMCPlan
// //           </h3>
// //           <Breadcrumb items={breadcrumbdata} />
// //         </div>
// //         <div className="col-md-12 grid-margin stretch-card p-0">
// //           <div className="card m-0">
// //             <div className="card-body">
// //               <form onSubmit={handleSubmit}>
// //                 <div className="row">
// //                   <div className="col-sm-4">
// //                     <div className="form-group">
// //                       <label
// //                         htmlFor="client-sortOrder"
// //                         className="col-form-label "
// //                       >
// //                         Sort Order
// //                       </label>
// //                       <input
// //                         type="number"
// //                         className={`border border-dark form-control  `}
// //                         id="client-sortOrder"
// //                         name="sortOrder"
// //                         placeholder="Enter client Sort Order"
// //                         min={1}
// //                         value={values.sortOrder}
// //                         onChange={handleChange}
// //                       />
// //                       {touched.sortOrder && errors.sortOrder && (
// //                         <small className="text-danger">
// //                           {errors.sortOrder}
// //                         </small>
// //                       )}
// //                     </div>
// //                   </div>

// //                   <div className="col-sm-4">
// //                     <div className="form-group">
// //                       <label htmlFor="amcplan-name" className="col-form-label ">
// //                         Name
// //                       </label>
// //                       <input
// //                         type="text"
// //                         className={`border border-dark form-control  `}
// //                         id="amcplan-name"
// //                         name="name"
// //                         placeholder="Enter amcplan name"
// //                         value={values.name || ""}
// //                         onChange={handleChange}
// //                         onBlur={handleBlur}
// //                       />
// //                       {touched.name && errors.name && (
// //                         <small className="text-danger">{errors.name}</small>
// //                       )}
// //                     </div>
// //                   </div>

// //                   <div className="col-sm-4">
// //                     <div className="form-group">
// //                       <label
// //                         htmlFor="amcplan-shortDescription"
// //                         className="col-form-label "
// //                       >
// //                         Short Description
// //                       </label>
// //                       <input
// //                         type="text"
// //                         className={`border border-dark form-control  `}
// //                         id="amcplan-shortDescription"
// //                         name="shortDescription"
// //                         placeholder="Enter amcplan shortDescription"
// //                         value={values.shortDescription || ""}
// //                         onChange={handleChange}
// //                         onBlur={handleBlur}
// //                       />
// //                     </div>
// //                   </div>

// //                   <div className="col-sm-4">
// //                     <div className="form-group">
// //                       <label
// //                         htmlFor="amcplan-monthlyPrice"
// //                         className="col-form-label "
// //                       >
// //                         Monthly Price
// //                       </label>
// //                       <input
// //                         type="text"
// //                         className={`border border-dark form-control  `}
// //                         id="amcplan-monthlyPrice"
// //                         name="monthlyPrice"
// //                         placeholder="Enter amcplan monthlyPrice"
// //                         value={values.monthlyPrice || ""}
// //                         onChange={handleChange}
// //                         onBlur={handleBlur}
// //                       />
// //                       {touched.monthlyPrice && errors.monthlyPrice && (
// //                         <small className="text-danger">
// //                           {errors.monthlyPrice}
// //                         </small>
// //                       )}
// //                     </div>
// //                   </div>

// //                   <div className="col-sm-4">
// //                     <div className="form-group">
// //                       <label
// //                         htmlFor="amcplan-monthlyPrice"
// //                         className="col-form-label "
// //                       >
// //                         Yearly Price
// //                       </label>
// //                       <input
// //                         type="text"
// //                         className={`border border-dark form-control  `}
// //                         id="amcplan-yearlyPrice"
// //                         name="yearlyPrice"
// //                         placeholder="Enter amcplan yearlyPrice"
// //                         value={values.yearlyPrice || ""}
// //                         onChange={handleChange}
// //                         onBlur={handleBlur}
// //                       />
// //                       {touched.yearlyPrice && errors.yearlyPrice && (
// //                         <small className="text-danger">
// //                           {errors.yearlyPrice}
// //                         </small>
// //                       )}
// //                     </div>
// //                   </div>

// //                   <div className="col-sm-4">
// //                     <div className="form-group">
// //                       <label
// //                         htmlFor="amcplan-badge"
// //                         className="col-form-label "
// //                       >
// //                         Badge
// //                       </label>
// //                       <input
// //                         type="text"
// //                         className={`border border-dark form-control  `}
// //                         id="amcplan-badge"
// //                         name="badge"
// //                         placeholder="Enter amcplan badge"
// //                         value={values.badge || ""}
// //                         onChange={handleChange}
// //                         onBlur={handleBlur}
// //                       />
// //                       {touched.badge && errors.badge && (
// //                         <small className="text-danger">
// //                           {errors.badge}
// //                         </small>
// //                       )}
// //                     </div>
// //                   </div>
// //                   <div className="col-sm-6">
// //                     <div className="form-group">
// //                       <label
// //                         htmlFor="slider-description"
// //                         className="col-form-label "
// //                       >
// //                         Description
// //                       </label>

// //                       <CodeEditor
// //                         value={values.description}
// //                         onChange={(val) => setFieldValue("description", val)}
// //                       />

// //                       {touched.description && errors.description && (
// //                         <small className="text-danger">
// //                           {errors.description}
// //                         </small>
// //                       )}
// //                     </div>
// //                   </div>
// //                   <div className="col-sm-6">
// //                     <ImageUploadDragAndDrop
// //                       id="slider-image"
// //                       label={`Icon (354px X 354px)`}
// //                       value={values.icon}
// //                       onChange={(file) => setFieldValue("icon", file)}
// //                       error={errors.icon}
// //                       touched={touched.icon}
// //                     />
// //                   </div>
// //                   <div className="col-md-12 mt-5">
// //                     <SwitchToggle
// //                       checked={values.isActive}
// //                       onChange={(val) => setFieldValue("isActive", val)}
// //                     />
// //                   </div>
// //                 </div>

// //                 <div className="text-center mt-4">
// //                   <Button type="submit" color="primary" disabled={loading}>
// //                     {loading ? (
// //                       <span className="d-inline-flex align-items-center gap-2">
// //                         <span
// //                           className="spinner-border spinner-border-sm"
// //                           role="status"
// //                           aria-hidden="true"
// //                         ></span>
// //                         Processing...
// //                       </span>
// //                     ) : (
// //                       "Save"
// //                     )}
// //                   </Button>

// //                   <Link to={`/amc-plans`}>
// //                     <Button color="danger mx-2">Cancel </Button>
// //                   </Link>
// //                 </div>
// //               </form>
// //             </div>
// //           </div>
// //         </div>
// //       </div>
// //     </>
// //   );
// // };

// // export default AddAmcPlan;
